<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { postActivityAttributes } from '@/Util/api';
  import { signed, fmtDate } from './format';
  import { bigDrop, breakthroughRows as buildRows } from './report';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  export let report: any;
  export let forUser: string | null = null;
  // Flagging recalculates the athlete's history in the background: the page is told, and reads the report again when that is done.
  export let onFlagged: () => void = () => {};
  // Counts up each time the page has read the report again after a recalculation: the message about it is then out of date.
  export let refreshed = 0;

  $: breakthroughRows = buildRows(report);

  // Flagging a breakthrough: the athlete's own activity only (not when a coach is looking).
  let flagged: Record<string, boolean> = {};
  let confirmFlag: string | null = null;
  let flagBusy = false;
  let flagMessage: string | null = null;
  $: if (refreshed) flagMessage = '';

  async function setFlag(path: string, value: boolean) {
    flagBusy = true;
    flagMessage = null;
    try {
      await postActivityAttributes({ path, flag: value ? 1 : 0 } as any);
      flagged = { ...flagged, [path]: value };
      confirmFlag = null;
      onFlagged();
      flagMessage = $_(value ? 'report.breakthroughs.flagged' : 'report.breakthroughs.unflagged');
    } catch (e) {
      flagMessage = $_(value ? 'report.breakthroughs.flag_failed' : 'report.breakthroughs.unflag_failed');
    } finally {
      flagBusy = false;
    }
  }
  // Flagged now, or flagged before (the report lists those); a change made on this page is remembered until it reloads.
  const isFlagged = (path: string, listed: boolean) => (path in flagged ? flagged[path] : listed);
</script>

  <section class="mb-6 rounded border border-zinc-200 bg-white p-4">
    <h2 class="text-lg font-semibold mb-3">{$_('report.breakthroughs.heading')} {#if report.breakthroughs?.summary?.count}<span class="text-zinc-400 font-normal">({report.breakthroughs.summary.count})</span>{/if}</h2>
    {#if !breakthroughRows.length}
      <p class="text-sm text-zinc-500">{$_('report.breakthroughs.none')}</p>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead><tr class="text-left text-zinc-500"><th class="font-normal py-1">{$_('report.breakthroughs.date')}</th><th class="font-normal">{$_('report.result.activity')}</th><th class="font-normal text-right">TP</th><th class="font-normal text-right">HIE</th><th class="font-normal text-right">PP</th>{#if !forUser}<th class="font-normal text-right w-10"><span class="sr-only">{$_('report.breakthroughs.flag')}</span></th>{/if}</tr></thead>
          <tbody>
            {#each breakthroughRows as b}
              {@const isFlag = isFlagged(b.activity_id, b.flaggedRow)}
              <tr class="border-t">
                <td class="py-1.5 whitespace-nowrap">{fmtDate(b.date)}</td>
                <td class="max-w-[14rem] truncate">{#if b.activity_id}<a class="text-blue-600" href="/activities/{b.activity_id}">{b.name ?? $_('report.result.activity')}</a>{:else}{b.name ?? $_('report.result.activity')}{/if}</td>
                {#each [['ftp', 1, 0], ['hie', 0.001, 1], ['pp', 1, 0]] as [key, scale, digits]}
                  <td class="text-right tabular-nums {!b.flaggedRow && bigDrop(b, key) ? 'text-red-700 font-semibold bg-red-50' : ''}" title={!b.flaggedRow && bigDrop(b, key) ? $_('report.breakthroughs.down_3') : undefined}>{b.flaggedRow ? '--' : b.after ? signed(((b.after[key] ?? 0) - (b.before?.[key] ?? b.after[key] ?? 0)) * scale, digits) : '–'}
                    {#if !b.flaggedRow && b.against_prediction?.after?.[key] !== undefined && b.against_prediction?.after?.[key] !== null}<span class="text-zinc-400 block text-xs">{$_('report.breakthroughs.vs_plan', { values: { change: signed(b.against_prediction.after[key] * scale, digits) } })}</span>{/if}</td>
                {/each}
                {#if !forUser}
                  <td class="text-right">
                    {#if b.activity_id}
                      <button class="p-1 bg-transparent border-0 {isFlag ? 'text-red-600' : 'text-zinc-400 hover:text-zinc-700'}" title={isFlag ? $_('report.breakthroughs.flagged_title') : $_('report.breakthroughs.flag_this')} aria-label={isFlag ? $_('report.breakthroughs.unflag_this') : $_('report.breakthroughs.flag_this')} disabled={flagBusy}
                        on:click={() => (confirmFlag = confirmFlag === b.activity_id ? null : b.activity_id)}><i class="{isFlag ? 'fa-solid' : 'fa-regular'} fa-flag" aria-hidden="true" /></button>
                    {/if}
                  </td>
                {/if}
              </tr>
              {#if confirmFlag === b.activity_id}
                <tr><td colspan="6" class="pb-3">
                  <div class="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-zinc-800">
                    <p class="mb-2">{$_(isFlag ? 'report.breakthroughs.confirm_unflag' : 'report.breakthroughs.confirm_flag', { values: { name: b.name ?? $_('report.breakthroughs.this_activity') } })} {$_('report.breakthroughs.confirm_tail')}</p>
                    <div class="flex gap-2">
                      <button class="rounded bg-zinc-800 text-white px-3 py-1.5" disabled={flagBusy} on:click={() => setFlag(b.activity_id, !isFlag)}>{flagBusy ? $_('report.breakthroughs.working') : isFlag ? $_('report.breakthroughs.unflag_it') : $_('report.breakthroughs.flag_it')}</button>
                      <button class="rounded border border-zinc-300 bg-white px-3 py-1.5" disabled={flagBusy} on:click={() => (confirmFlag = null)}>{$_('report.breakthroughs.cancel')}</button>
                    </div>
                  </div>
                </td></tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>
      <p class="mt-3 text-xs text-zinc-500">{$_('report.breakthroughs.note_a')} <span class="rounded bg-red-50 px-1 font-semibold text-red-700">{$_('report.breakthroughs.red')}</span> {$_('report.breakthroughs.note_b')}</p>
    {/if}
    {#if flagMessage}<p class="mt-2 text-sm text-zinc-700">{flagMessage}</p>{/if}

  </section>
